import {
    Multiselect,
    MultiselectProps,
    Select,
    SelectProps
} from '@cloudscape-design/components';
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalState } from '../../pages/context/GlobalStateContext';

// --- CORE TYPES (Based on the original structure you provided) ---
type ItemUserType = {
    idUserType: number;
    userType: string;
    value: string; // Cloudscape value (idUserType.toString())
    label: string; // Cloudscape label (userType)
    showId?: boolean;
};

// --- PROPS INTERFACES ---

// Base props common to both modes
interface SelectUserTypeBaseProps {
    visible?: boolean;
    readonly?: boolean;
    width?: number;
    tokenLimit?: number;
    hideTokens?: boolean;
    showId?: boolean;
}

// 1. When single = true
interface SelectUserTypeSingleProps extends SelectUserTypeBaseProps {
    single: true;
    value?: number; // Single ID
    onSelect?: (selected: number | null) => void; // Returns ID or null
}

// 2. When single = false (or undefined)
interface SelectUserTypeMultiProps extends SelectUserTypeBaseProps {
    single?: false; // default is "false"
    value?: number[]; // Array of IDs
    onSelect?: (selected: number[]) => void; // Returns array of IDs
}

export type SelectUserTypeProps =
    | SelectUserTypeSingleProps
    | SelectUserTypeMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectUserType: React.FC<SelectUserTypeProps> = (props) => {
    const {
        single,
        value,
        width = 200,
        readonly,
        visible,
        tokenLimit = 3,
        hideTokens = false,
        showId = false,
        onSelect
    } = props;

    const { data } = useGlobalState();
    if (!data || !data.usersTypes) return <></>;

    // State for Single Selection
    const [selectedSingle, setSelectedSingle] = useState<ItemUserType | null>(null);

    // State for Multi Selection
    const [selectedMulti, setSelectedMulti] = useState<ItemUserType[]>([]);


    // --- Mapped User Types (Options List) ---
    const mappedUserTypes: ItemUserType[] = useMemo(() => {
        return data.usersTypes.map((ut: any) => ({
            idUserType: ut.idUserType,
            userType: ut.userType,
            value: ut.idUserType.toString(),
            label: ut.userType + (showId ? ` (${ut.idUserType})` : "")
        }));
    }, [data.usersTypes]);


    // --- Initialization & Value Sync Effect ---
    // This is the critical section that must react to the parent setting value=[]
    useEffect(() => {
        const valueArray = Array.isArray(value) ? value : (value !== undefined && value !== null ? [value] : []);

        // If the value is undefined, null, or [], reset the internal state
        if (value === undefined || value === null || valueArray.length === 0) {
            setSelectedSingle(null);
            setSelectedMulti([]);
            return;
        }

        if (single) {
            // SINGLE MODE
            const id = value as number;
            const match = mappedUserTypes.find(ut => ut.idUserType === id) || null;
            setSelectedSingle(match);

        } else {
            // MULTI MODE: Value is an array of numbers (IDs)
            const ids = value as number[];
            const matches = mappedUserTypes.filter(ut => ids.includes(ut.idUserType));
            setSelectedMulti(matches);
        }
    }, [value, single, mappedUserTypes]);


    // --- Single Select Handler ---
    const handleSingleChange: SelectProps['onChange'] = ({ detail }) => {
        const selectedOption = mappedUserTypes.find(opt => opt.value === detail.selectedOption.value) || null;

        setSelectedSingle(selectedOption);

        if (onSelect && single) {
            const selectedId = selectedOption?.idUserType ?? null;
            (onSelect as (selected: number | null) => void)(selectedId);
        }
    };

    // --- Multi Select Handler ---
    const handleMultiChange: MultiselectProps['onChange'] = ({ detail }) => {
        // Find the full ItemUserType objects based on the selected options (which are OptionDefinition)
        const list = detail.selectedOptions.map(opt =>
            mappedUserTypes.find(ut => ut.value === opt.value)
        ).filter((x): x is ItemUserType => x !== undefined);

        setSelectedMulti(list);

        if (onSelect && !single) {
            const selectedIds = list.map(item => item.idUserType);
            (onSelect as (selected: number[]) => void)(selectedIds);
        }
    };

    // --- Rendering ---

    const placeholder = single ? "Seleccionar tipo de usuario" : selectedMulti && selectedMulti.length > 0 ? `${selectedMulti.length} seleccionado${selectedMulti.length > 1 ? 's' : ''}` : "Seleccionar tipos de usuario";
    const wrapperStyle: React.CSSProperties = {
        width: width > 0 ? `${width}px` : '100%'
    };
    return (
        <div style={wrapperStyle}>
            {single ? (
                // SINGLE SELECTION
                <Select
                    expandToViewport
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOption={selectedSingle}
                    options={mappedUserTypes}
                    onChange={handleSingleChange}
                />
            ) : (
                // MULTI SELECTION
                <Multiselect
                    expandToViewport
                    hideTokens={hideTokens}
                    tokenLimit={tokenLimit}
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOptions={selectedMulti}
                    options={mappedUserTypes}
                    onChange={handleMultiChange}
                />
            )}
        </div>
    );
};

export default SelectUserType;